// src/pages/seasons/RegistrationsDashboard.jsx
'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  doc, getDoc, getDocs, collection, query, where, onSnapshot, orderBy, limit,
  updateDoc, serverTimestamp, deleteDoc, addDoc, writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { calculatePrice, fmtMoney } from '@/utils/pricing';
import { formatTierPrice } from '@/utils/pricingTiers';
import { isValidUsE164 } from '@/utils/phone';
import { db } from '@/firebase/firebase';
import { format } from 'date-fns';
import { es as esLocale } from 'date-fns/locale';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { Button } from '@/components/button';
import { Input } from '@/components/input';
import BroadcastSmsModal from '@/components/coach/BroadcastSmsModal';
import { Heading, Subheading } from '@/components/heading';
import { Text } from '@/components/text';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHead, TableBody, TableRow, TableHeader, TableCell
} from '@/components/table';

import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  LinkIcon,
  XMarkIcon,
  PencilSquareIcon,
  PhoneIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  SparklesIcon,
  BanknotesIcon,
  UserPlusIcon,
  DocumentCheckIcon,
  ClipboardDocumentIcon,
  CloudArrowUpIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import TeamSubNav from '@/components/team/TeamSubNav';

/* ------------------------------------------------------------------ */
/* Bilingual strings                                                    */
/* ------------------------------------------------------------------ */
const T = {
  en: {
    free: 'Free',
    statuses: {
      confirmed: 'Confirmed',
      pending_payment: 'Pending Payment',
      paid: 'Paid',
      cancelled: 'Cancelled',
      archived: 'Archived',
    },
    docLabels: {
      birth_certificate: 'Birth Certificate',
      player_photo: 'Player Photo',
      club_registration: 'Club Registration',
      medical_form: 'Medical Form',
      insurance_card: 'Insurance Card',
    },
    archivedNotice: 'This registration has been archived and is hidden from the main list.',
    player: 'Player',
    name: 'Name',
    dob: 'Date of Birth',
    parentGuardian: 'Parent / Guardian',
    email: 'Email',
    phone: 'Phone',
    smsOptInStatus: {
      on:           'Opted in to SMS — will receive text alerts',
      off:          'Not opted in to SMS',
      badPhone:     'Opted in but phone number is missing or not a valid US number — texts will not deliver',
      badPhoneShort: 'Bad #',
    },
    emergencyContact: 'Emergency Contact',
    relationship: 'Relationship',
    medicalNotes: 'Medical Notes',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    noneNoted: 'None noted',
    pricing: 'Pricing',
    applyAid: 'Apply Aid',
    basePrice: 'Base price',
    earlyBird: 'Early bird',
    siblingDiscount: 'Sibling discount',
    returningPlayer: 'Returning player',
    financialAid: 'Financial aid',
    total: 'Total',
    installmentPlan: 'installment plan',
    firstPayment: 'first payment',
    applyFinancialAid: 'Apply Financial Aid',
    discountDollars: 'discount in dollars',
    reasonOptional: 'Reason (optional)…',
    paymentPlan: 'Payment Plan',
    installments: 'installments',
    installment: 'Installment',
    paidViaStripe: 'Paid via Stripe',
    paidLabel: 'Paid',
    markPaid: 'Mark Paid',
    pendingLabel: 'Pending',
    scheduledLabel: 'Scheduled',
    failedLabel: 'Failed',
    pastDueLabel: 'Past due',
    retryNow: 'Retry now',
    retrying: 'Retrying…',
    retrySuccess: 'Charge succeeded — $',
    retryFailed: 'Retry failed',
    nextChargeLabel: 'Next charge',
    attemptsLabel: 'attempts',
    refundedLabel: 'Refunded',
    partiallyRefundedLabel: 'Partially refunded',
    cancelModalTitle: 'Cancel registration',
    cancelModalIntro: 'Stops future charges. Paid amounts can be refunded.',
    cancelRefundFull: 'Refund full amount paid',
    cancelRefundPartial: 'Refund a specific amount',
    cancelRefundNone: 'Cancel without refund',
    cancelRefundNoneHint: 'No money moves. Use this for non-refundable deposits.',
    cancelPaidSoFar: 'Paid so far',
    cancelAmountPlaceholder: 'Amount in dollars',
    cancelReasonPlaceholder: 'Reason (optional, internal)…',
    cancelConfirmBtn: 'Cancel registration',
    cancelingBtn: 'Cancelling…',
    cancelToastSuccess: 'Registration cancelled',
    cancelToastRefunded: 'Cancelled. Refunded ',
    cancelToastFail: 'Cancel failed',
    cancelKeepBtn: 'Keep registration',
    cancelExceedsPaid: 'Amount exceeds paid total',
    documents: 'Documents',
    copyUploadLink: 'Copy Upload Link',
    missing: 'Missing',
    viewDoc: 'View',
    sendUploadLinkToParent: 'Send Upload Link to Parent',
    registrationInfo: 'Registration Info',
    smsHistory: {
      title: 'SMS History',
      loading: 'Loading…',
      empty: 'No texts sent to this parent yet.',
      statusSent:   'Sent',
      statusFailed: 'Failed',
      urgent: 'Urgent',
      flows: {
        registration_confirmation: 'Confirmation',
        installment_reminder:      'Payment reminder',
        installment_soft_failure:  'Payment failed (retry)',
        installment_final_failure: 'Payment failed (final)',
        coach_broadcast:           'Coach broadcast',
        weather_cancellation:      'Weather alert',
        unknown:                   'Other',
      },
    },
    division: 'Division',
    registered: 'Registered',
    waiverSignedBy: 'Waiver Signed By',
    deletePermanently: 'Delete permanently?',
    deleteWarnPrefix: 'This will remove all data for',
    deleteWarnSuffix: 'and cannot be undone.',
    deleting: 'Deleting…',
    yesDelete: 'Yes, Delete',
    addToRoster: 'Add to Roster',
    addedToRoster: 'Added to roster',
    resendEmail: 'Resend Confirmation Email',
    sending: 'Sending…',
    markConfirmed: 'Mark Confirmed',
    markCancelled: 'Mark Cancelled',
    reinstate: 'Reinstate',
    reinstating: 'Reinstating…',
    reinstateSuccess: 'Registration reinstated',
    reinstateFail: 'Failed to reinstate',
    reEnrollNote: 'A refund was issued — to re-enroll, send the registration link.',
    sendRegLinkBtn: 'Copy Re-enrollment Link',
    updating: 'Updating…',
    archiveBtn: 'Archive',
    restoreRegistration: 'Restore Registration',
    deleteBtn: 'Delete',
    backToSeasons: 'Back to Seasons',
    registrationsFallback: 'Registrations',
    addAllToRoster: 'Add All to Roster',
    copyRegLink: 'Copy Reg Link',
    copyEmails: 'Copy Emails',
    exportCSV: 'Export CSV',
    textParents: 'Text Parents',
    textParentsTooltip: '{reachable} of {total} parents are opted in to SMS with a valid phone number.',
    broadcast: {
      _locale: 'en',
      title: 'Text parents on this roster',
      recipientsLabel: 'Parents who\'ll receive this text',
      noEligibleHint: 'No parents on this roster have opted in to SMS with a valid phone number. Ask them to re-register or update their phone to enable text alerts.',
      messageLabel: 'Your message',
      messagePlaceholder: 'E.g. Practice today is moved to Field 3 due to sprinklers on Field 1. Same start time.',
      tooLong: 'Too long — please shorten.',
      footerPreviewLabel: 'Appended automatically:',
      urgentLabel: 'Mark as urgent',
      urgentHint: 'Urgent texts bypass quiet hours (9pm–8am). Use only for genuinely time-sensitive alerts like last-minute cancellations.',
      confirmUrgent: 'This will send NOW even if a parent is in their quiet-hours window (9pm–8am). Continue?',
      cancel: 'Cancel',
      send: 'Send text',
      sending: 'Sending…',
      toastSent: 'Text sent to {n} parents',
      toastPartial: 'Sent to {sent}; {failed} failed. Check logs.',
      toastNoRecipients: 'No eligible parents — nothing sent.',
      toastForbidden: 'Only team coaches can send broadcasts.',
      toastGenericError: 'Broadcast failed. Please try again.',
      historyTitle: 'Recent broadcasts',
      historyLoading: 'Loading…',
      historyEmpty: 'No broadcasts yet for this team.',
      historyRefresh: 'Refresh',
      historyCounts: 'Sent to {sent} of {total}',
      historyFailedSuffix: '{n} failed',
      historyUrgentBadge: 'Urgent',
      historyStatusLabels: {
        sent: 'Sent',
        partial: 'Partial',
        failed: 'Failed',
        no_recipients: 'No one',
        sending: 'Sending',
      },
    },
    stats: {
      activeTotal: 'Active Total',
      confirmed: 'Paid & Confirmed',
      pending: 'Pending',
      revenue: 'Collected',
      contracted: '{amount} contracted',
      docsMissing: 'Docs Missing',
      players: 'players',
    },
    searchPlaceholder: 'Search player, parent, email…',
    filterTabs: {
      all: 'All Active',
      confirmed: 'Confirmed',
      pending_payment: 'Pending Payment',
      paid: 'Paid',
      past_due: 'Past Due',
      cancelled: 'Cancelled',
      archived: 'Archived',
    },
    noArchived: 'No archived registrations',
    noRegistrations: 'No registrations yet',
    noArchivedBody: 'Archived registrations will appear here.',
    noRegistrationsBody: 'Share the registration link with parents to get started.',
    copyRegistrationLink: 'Copy Registration Link',
    tableHeaders: {
      player: 'Player',
      dob: 'DOB',
      parent: 'Parent',
      contact: 'Contact',
      status: 'Status',
      docs: 'Docs',
      registered: 'Registered',
    },
    programPill: 'Program',
    medicalPill: 'Medical',
    showing: 'Showing',
    of: 'of',
    totalLabel: 'total',
    includingProgram: 'incl.',
    programLabel: 'program',
    archivedCountSuffix: 'archived',
    toasts: {
      statusUpdatedTo: 'Status updated to',
      failUpdateStatus: 'Failed to update status',
      notesSaved: 'Notes saved',
      failSaveNotes: 'Failed to save notes',
      regDeleted: 'Registration deleted permanently.',
      failDelete: 'Failed to delete registration',
      aidApplied: 'Financial aid applied.',
      failSave: 'Failed to save.',
      installmentMarkedPrefix: 'Installment',
      installmentMarkedSuffix: 'marked as paid.',
      failInstallment: 'Failed to update installment.',
      noEmail: 'No email address on file',
      emailResentTo: 'Confirmation email resent to',
      failSendEmail: 'Failed to send email',
      noEmailsToCopy: 'No emails to copy.',
      emailCopied: 'email copied to clipboard!',
      emailsCopied: 'emails copied to clipboard!',
      regLinkCopied: 'Registration link copied!',
      uploadLinkCopied: 'Upload link copied!',
      noTeamLinked: 'No team linked to this season.',
      playerAddedToRoster: 'added to roster!',
      linkedToRoster: 'is already on the roster — linked to this registration.',
      couldNotAddRoster: 'Could not add to roster. Try again.',
      allAlreadyOnRoster: 'All confirmed players are already on the roster.',
      playersAddedSingular: 'player added to roster!',
      playersAddedPlural: 'players added to roster!',
      batchFailed: 'Batch write failed. Try again.',
      exportedPrefix: 'Exported',
      exportedSuffix: 'registrations.',
    },
  },
  es: {
    free: 'Gratis',
    statuses: {
      confirmed: 'Confirmado',
      pending_payment: 'Pago Pendiente',
      paid: 'Pagado',
      cancelled: 'Cancelado',
      archived: 'Archivado',
    },
    docLabels: {
      birth_certificate: 'Acta de Nacimiento',
      player_photo: 'Foto del Jugador',
      club_registration: 'Registro del Club',
      medical_form: 'Formulario Médico',
      insurance_card: 'Tarjeta de Seguro',
    },
    archivedNotice: 'Este registro ha sido archivado y está oculto de la lista principal.',
    player: 'Jugador',
    name: 'Nombre',
    dob: 'Fecha de Nacimiento',
    parentGuardian: 'Padre / Tutor',
    email: 'Correo',
    phone: 'Teléfono',
    smsOptInStatus: {
      on:           'Inscrito a SMS — recibirá alertas de texto',
      off:          'No inscrito a SMS',
      badPhone:     'Inscrito pero el número no es válido — los mensajes no se entregarán',
      badPhoneShort: 'Núm. inválido',
    },
    emergencyContact: 'Contacto de Emergencia',
    relationship: 'Parentesco',
    medicalNotes: 'Notas Médicas',
    edit: 'Editar',
    save: 'Guardar',
    cancel: 'Cancelar',
    noneNoted: 'Nada anotado',
    pricing: 'Precio',
    applyAid: 'Aplicar Ayuda',
    basePrice: 'Precio base',
    earlyBird: 'Inscripción anticipada',
    siblingDiscount: 'Descuento por hermano',
    returningPlayer: 'Jugador recurrente',
    financialAid: 'Ayuda financiera',
    total: 'Total',
    installmentPlan: 'plan de pagos',
    firstPayment: 'primer pago',
    applyFinancialAid: 'Aplicar Ayuda Financiera',
    discountDollars: 'descuento en dólares',
    reasonOptional: 'Razón (opcional)…',
    paymentPlan: 'Plan de Pagos',
    installments: 'pagos',
    installment: 'Pago',
    paidViaStripe: 'Pagado por Stripe',
    paidLabel: 'Pagado',
    markPaid: 'Marcar Pagado',
    pendingLabel: 'Pendiente',
    scheduledLabel: 'Programado',
    failedLabel: 'Falló',
    pastDueLabel: 'Atrasado',
    retryNow: 'Reintentar',
    retrying: 'Reintentando…',
    retrySuccess: 'Cobro exitoso — $',
    refundedLabel: 'Reembolsado',
    partiallyRefundedLabel: 'Reembolso parcial',
    cancelModalTitle: 'Cancelar registro',
    cancelModalIntro: 'Detiene los cobros futuros. Los montos pagados pueden reembolsarse.',
    cancelRefundFull: 'Reembolsar todo lo pagado',
    cancelRefundPartial: 'Reembolsar un monto específico',
    cancelRefundNone: 'Cancelar sin reembolso',
    cancelRefundNoneHint: 'No se mueve dinero. Úsalo para depósitos no reembolsables.',
    cancelPaidSoFar: 'Pagado hasta ahora',
    cancelAmountPlaceholder: 'Monto en dólares',
    cancelReasonPlaceholder: 'Razón (opcional, interna)…',
    cancelConfirmBtn: 'Cancelar registro',
    cancelingBtn: 'Cancelando…',
    cancelToastSuccess: 'Registro cancelado',
    cancelToastRefunded: 'Cancelado. Reembolsado ',
    cancelToastFail: 'Error al cancelar',
    cancelKeepBtn: 'Mantener registro',
    cancelExceedsPaid: 'El monto excede lo pagado',
    retryFailed: 'Reintento falló',
    nextChargeLabel: 'Próximo cobro',
    attemptsLabel: 'intentos',
    documents: 'Documentos',
    copyUploadLink: 'Copiar Enlace de Subida',
    missing: 'Faltante',
    viewDoc: 'Ver',
    sendUploadLinkToParent: 'Enviar Enlace de Subida al Padre',
    registrationInfo: 'Información de Registro',
    smsHistory: {
      title: 'Historial de SMS',
      loading: 'Cargando…',
      empty: 'Aún no se han enviado mensajes a este padre.',
      statusSent:   'Enviado',
      statusFailed: 'Falló',
      urgent: 'Urgente',
      flows: {
        registration_confirmation: 'Confirmación',
        installment_reminder:      'Recordatorio de pago',
        installment_soft_failure:  'Pago fallido (reintento)',
        installment_final_failure: 'Pago fallido (final)',
        coach_broadcast:           'Envío del entrenador',
        weather_cancellation:      'Alerta meteorológica',
        unknown:                   'Otro',
      },
    },
    division: 'División',
    registered: 'Registrado',
    waiverSignedBy: 'Exención Firmada Por',
    deletePermanently: '¿Eliminar permanentemente?',
    deleteWarnPrefix: 'Esto eliminará todos los datos de',
    deleteWarnSuffix: 'y no se puede deshacer.',
    deleting: 'Eliminando…',
    yesDelete: 'Sí, Eliminar',
    addToRoster: 'Agregar al Equipo',
    addedToRoster: 'Agregado al equipo',
    resendEmail: 'Reenviar Correo de Confirmación',
    sending: 'Enviando…',
    markConfirmed: 'Marcar Confirmado',
    markCancelled: 'Marcar Cancelado',
    reinstate: 'Reactivar',
    reinstating: 'Reactivando…',
    reinstateSuccess: 'Registro reactivado',
    reinstateFail: 'Error al reactivar',
    reEnrollNote: 'Se emitió un reembolso — para reinscribir, envía el enlace de registro.',
    sendRegLinkBtn: 'Copiar enlace de reinscripción',
    updating: 'Actualizando…',
    archiveBtn: 'Archivar',
    restoreRegistration: 'Restaurar Registro',
    deleteBtn: 'Eliminar',
    backToSeasons: 'Volver a Temporadas',
    registrationsFallback: 'Registros',
    addAllToRoster: 'Agregar Todos al Equipo',
    copyRegLink: 'Copiar Enlace',
    copyEmails: 'Copiar Correos',
    exportCSV: 'Exportar CSV',
    textParents: 'Enviar SMS',
    textParentsTooltip: '{reachable} de {total} padres están suscritos a SMS con un teléfono válido.',
    broadcast: {
      _locale: 'es',
      title: 'Enviar SMS a los padres de este equipo',
      recipientsLabel: 'Padres que recibirán el mensaje',
      noEligibleHint: 'Ningún padre de este equipo tiene SMS activados ni un teléfono válido. Pídales volver a registrarse o actualizar su teléfono para recibir alertas.',
      messageLabel: 'Tu mensaje',
      messagePlaceholder: 'Ej. La práctica de hoy se mueve a la Cancha 3 por riego en la Cancha 1. Misma hora.',
      tooLong: 'Demasiado largo — acórtalo.',
      footerPreviewLabel: 'Se agrega automáticamente:',
      urgentLabel: 'Marcar como urgente',
      urgentHint: 'Los mensajes urgentes ignoran el horario silencioso (9pm–8am). Úsalo solo para avisos realmente urgentes como cancelaciones de última hora.',
      confirmUrgent: 'Esto enviará AHORA incluso si el padre está en horario silencioso (9pm–8am). ¿Continuar?',
      cancel: 'Cancelar',
      send: 'Enviar SMS',
      sending: 'Enviando…',
      toastSent: 'SMS enviado a {n} padres',
      toastPartial: 'Enviado a {sent}; {failed} fallaron. Revisa los registros.',
      toastNoRecipients: 'Ningún padre elegible — no se envió nada.',
      toastForbidden: 'Solo los entrenadores del equipo pueden enviar anuncios.',
      toastGenericError: 'Falló el envío. Inténtalo de nuevo.',
      historyTitle: 'Envíos recientes',
      historyLoading: 'Cargando…',
      historyEmpty: 'Aún no hay envíos para este equipo.',
      historyRefresh: 'Actualizar',
      historyCounts: 'Enviado a {sent} de {total}',
      historyFailedSuffix: '{n} fallaron',
      historyUrgentBadge: 'Urgente',
      historyStatusLabels: {
        sent: 'Enviado',
        partial: 'Parcial',
        failed: 'Falló',
        no_recipients: 'Nadie',
        sending: 'Enviando',
      },
    },
    stats: {
      activeTotal: 'Total Activos',
      confirmed: 'Pagado y Confirmado',
      pending: 'Pendientes',
      revenue: 'Recaudado',
      contracted: '{amount} contratado',
      docsMissing: 'Docs Faltantes',
      players: 'jugadores',
    },
    searchPlaceholder: 'Buscar jugador, padre, correo…',
    filterTabs: {
      all: 'Todos Activos',
      confirmed: 'Confirmados',
      pending_payment: 'Pago Pendiente',
      paid: 'Pagados',
      past_due: 'Atrasados',
      cancelled: 'Cancelados',
      archived: 'Archivados',
    },
    noArchived: 'No hay registros archivados',
    noRegistrations: 'Aún no hay registros',
    noArchivedBody: 'Los registros archivados aparecerán aquí.',
    noRegistrationsBody: 'Comparte el enlace de registro con los padres para empezar.',
    copyRegistrationLink: 'Copiar Enlace de Registro',
    tableHeaders: {
      player: 'Jugador',
      dob: 'Nacimiento',
      parent: 'Padre',
      contact: 'Contacto',
      status: 'Estado',
      docs: 'Docs',
      registered: 'Registrado',
    },
    programPill: 'Programa',
    medicalPill: 'Médico',
    showing: 'Mostrando',
    of: 'de',
    totalLabel: 'en total',
    includingProgram: 'incl.',
    programLabel: 'programa',
    archivedCountSuffix: 'archivados',
    toasts: {
      statusUpdatedTo: 'Estado actualizado a',
      failUpdateStatus: 'Error al actualizar el estado',
      notesSaved: 'Notas guardadas',
      failSaveNotes: 'Error al guardar las notas',
      regDeleted: 'Registro eliminado permanentemente.',
      failDelete: 'Error al eliminar el registro',
      aidApplied: 'Ayuda financiera aplicada.',
      failSave: 'Error al guardar.',
      installmentMarkedPrefix: 'Pago',
      installmentMarkedSuffix: 'marcado como pagado.',
      failInstallment: 'Error al actualizar el pago.',
      noEmail: 'No hay correo registrado',
      emailResentTo: 'Correo de confirmación reenviado a',
      failSendEmail: 'Error al enviar el correo',
      noEmailsToCopy: 'No hay correos para copiar.',
      emailCopied: 'correo copiado al portapapeles!',
      emailsCopied: 'correos copiados al portapapeles!',
      regLinkCopied: '¡Enlace de registro copiado!',
      uploadLinkCopied: '¡Enlace de subida copiado!',
      noTeamLinked: 'No hay equipo vinculado a esta temporada.',
      playerAddedToRoster: 'agregado al equipo!',
      linkedToRoster: 'ya está en el equipo — vinculado a esta inscripción.',
      couldNotAddRoster: 'No se pudo agregar al equipo. Intenta de nuevo.',
      allAlreadyOnRoster: 'Todos los jugadores confirmados ya están en el equipo.',
      playersAddedSingular: 'jugador agregado al equipo!',
      playersAddedPlural: 'jugadores agregados al equipo!',
      batchFailed: 'Error al guardar los cambios. Intenta de nuevo.',
      exportedPrefix: 'Exportados',
      exportedSuffix: 'registros.',
    },
  },
};

/* ── design tokens (lime/pitch system) ────────────────── */
const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ── helpers ─────────────────────────────────────────── */
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

function formatFee(cents, tr) {
  if (!cents) return tr.free;
  return `$${(cents / 100).toFixed(2)}`;
}

/** Split "First Last" → { firstName, lastName } */
function splitName(fullName = '') {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  const lastName = parts.pop();
  return { firstName: parts.join(' '), lastName };
}

const STATUS_VARIANTS = {
  confirmed:       'default',
  pending_payment: 'secondary',
  paid:            'default',
  cancelled:       'outline',
  archived:        'outline',
};

/* Status pill — semantic colors per design system:
   emerald = paid/confirmed, amber = pending, red = cancelled, zinc = archived. */
const STATUS_PILL_CLASSES = {
  confirmed:       'bg-emerald-50 text-emerald-700 border-emerald-200',
  paid:            'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled:       'bg-red-50 text-red-700 border-red-200',
  archived:        'bg-zinc-50 text-zinc-600 border-zinc-200',
};

function StatusBadge({ status, tr }) {
  const label = tr.statuses[status] ?? status;
  const cls = STATUS_PILL_CLASSES[status] ?? 'bg-zinc-50 text-zinc-600 border-zinc-200';
  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border',
        cls
      )}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
    >
      {label}
    </span>
  );
}

/* ── doc icons ───────────────────────────────────────── */
const DOC_ICONS = {
  birth_certificate: '🪪',
  player_photo:      '📷',
  club_registration: '📋',
  medical_form:      '🩺',
  insurance_card:    '🛡️',
};

/** Pill showing "X/Y docs" for a single registration row */
function DocStatusPill({ reg, season }) {
  const required = season?.requiredDocuments ?? [];
  if (required.length === 0) return <span className="text-xs text-zinc-300">—</span>;
  const uploaded = required.filter(k => reg.documents?.[k]);
  const all = uploaded.length === required.length;
  const none = uploaded.length === 0;
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
      all  ? 'bg-green-100 text-green-700' :
      none ? 'bg-red-50 text-red-500' :
             'bg-amber-100 text-amber-700'
    )}>
      {all ? <CheckCircleIcon className="h-3 w-3" /> : <CloudArrowUpIcon className="h-3 w-3" />}
      {uploaded.length}/{required.length}
    </span>
  );
}

/* ── stat card (lime/pitch design system) ─────────────── */
function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
        <Icon className="h-4 w-4 text-[#5a8a1f]" />
      </div>
      <div className="min-w-0">
        <p
          className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
          style={{ fontFamily: HEAD_FONT }}
        >
          {label}
        </p>
        <p
          className="text-2xl font-bold text-[#0d1f14] leading-tight"
          style={{ fontFamily: HEAD_FONT }}
        >
          {value}
        </p>
        {sub && <p className="text-xs text-zinc-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

/* ── sortable column header ──────────────────────────── */
function SortableHeader({ field, currentField, dir, onSort, children, className }) {
  const active = field === currentField;
  return (
    <TableHeader
      className={clsx('cursor-pointer select-none', className)}
      onClick={() => onSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        {active ? (
          dir === 'asc'
            ? <ChevronUpIcon className="h-3 w-3 text-brand-500 shrink-0" />
            : <ChevronDownIcon className="h-3 w-3 text-brand-500 shrink-0" />
        ) : (
          <ChevronUpDownIcon className="h-3 w-3 text-zinc-300 shrink-0" />
        )}
      </span>
    </TableHeader>
  );
}

/* ── registration detail panel ───────────────────────── */
function RegistrationPanel({ reg, season, onClose, onStatusChange, onDelete, onAddToRoster, tr, dateLocale }) {
  const [changingStatus, setChangingStatus] = useState(false);
  const [editNotes, setEditNotes]           = useState(false);
  const [notes, setNotes]                   = useState(reg?.medicalNotes || '');
  const [confirmDelete, setConfirmDelete]   = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [retryingIdx, setRetryingIdx]         = useState(null);
  const [reinstating, setReinstating]         = useState(false);
  // Cancel + refund modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling]           = useState(false);
  const [refundMode, setRefundMode]           = useState('full'); // 'full' | 'partial' | 'none'
  const [partialAmount, setPartialAmount]     = useState('');
  const [cancelReason, setCancelReason]       = useState('');
  // Financial aid editor
  const [editAid, setEditAid]               = useState(false);
  const [aidAmount, setAidAmount]           = useState(
    reg?.pricing?.financialAidDiscount ? (reg.pricing.financialAidDiscount / 100).toFixed(2) : ''
  );
  const [aidNote, setAidNote]               = useState(reg?.financialAidNote || '');

  // SMS send history — last 10 entries for this registration. Read once
  // on mount; coach can manually refresh via the panel's SMS section.
  const [smsLogs, setSmsLogs]         = useState([]);
  const [smsLogsLoading, setSmsLogsLoading] = useState(false);

  useEffect(() => {
    if (!reg?.id) return;
    let cancelled = false;
    setSmsLogsLoading(true);
    getDocs(
      query(
        collection(db, 'registrations', reg.id, 'smsLogs'),
        orderBy('sentAt', 'desc'),
        limit(10)
      )
    ).then((snap) => {
      if (cancelled) return;
      setSmsLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }).catch(() => {
      // Non-fatal — absence of history is not an error state in the panel.
    }).finally(() => {
      if (!cancelled) setSmsLogsLoading(false);
    });
    return () => { cancelled = true; };
  }, [reg?.id]);

  if (!reg) return null;

  const changeStatus = async (newStatus) => {
    setChangingStatus(true);
    try {
      await updateDoc(doc(db, 'registrations', reg.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      toast.success(`${tr.toasts.statusUpdatedTo} ${tr.statuses[newStatus] ?? newStatus}`);
      onStatusChange(reg.id, newStatus);
    } catch {
      toast.error(tr.toasts.failUpdateStatus);
    } finally {
      setChangingStatus(false);
    }
  };

  const saveNotes = async () => {
    try {
      await updateDoc(doc(db, 'registrations', reg.id), { medicalNotes: notes });
      toast.success(tr.toasts.notesSaved);
      setEditNotes(false);
    } catch {
      toast.error(tr.toasts.failSaveNotes);
    }
  };

  const handleDelete = async () => {
    setChangingStatus(true);
    try {
      await deleteDoc(doc(db, 'registrations', reg.id));
      toast.success(tr.toasts.regDeleted);
      onDelete(reg.id);
    } catch {
      toast.error(tr.toasts.failDelete);
      setChangingStatus(false);
    }
  };

  const saveFinancialAid = async () => {
    const cents = Math.round(Number(aidAmount || 0) * 100);
    const base  = reg.pricing?.baseAmount ?? reg.amountDue ?? 0;
    const otherDiscounts = (reg.pricing?.earlyBirdSaving ?? 0) +
      (reg.pricing?.siblingDiscount ?? 0) + (reg.pricing?.returningDiscount ?? 0);
    const newFinal = Math.max(0, base - otherDiscounts - cents);
    try {
      await updateDoc(doc(db, 'registrations', reg.id), {
        'pricing.financialAidDiscount': cents,
        'pricing.totalDiscount': otherDiscounts + cents,
        'pricing.finalAmount': newFinal,
        financialAidNote: aidNote.trim() || null,
        updatedAt: serverTimestamp(),
      });
      toast.success(tr.toasts.aidApplied);
      setEditAid(false);
      onStatusChange(reg.id, reg.status); // trigger re-render
    } catch {
      toast.error(tr.toasts.failSave);
    }
  };

  const markInstallmentPaid = async () => {
    const next = (reg.installmentsPaid ?? 0) + 1;
    try {
      await updateDoc(doc(db, 'registrations', reg.id), {
        installmentsPaid: next,
        updatedAt: serverTimestamp(),
      });
      toast.success(`${tr.toasts.installmentMarkedPrefix} ${next} ${tr.toasts.installmentMarkedSuffix}`);
      onStatusChange(reg.id, reg.status);
    } catch {
      toast.error(tr.toasts.failInstallment);
    }
  };

  /**
   * Coach-triggered retry of a failed/past-due installment. Calls the
   * `retryInstallmentCharge` callable, which re-runs the saved payment
   * method and updates the schedule atomically on the server. The
   * onSnapshot listener picks up the update — no manual refresh needed.
   */
  const retryInstallment = async (installmentIndex) => {
    setRetryingIdx(installmentIndex);
    try {
      const res = await httpsCallable(getFunctions(), 'retryInstallmentCharge')({
        registrationId: reg.id,
        installmentIndex,
      });
      const status = res?.data?.status;
      if (status === 'paid' || status === 'already_paid') {
        const slot = reg.paymentPlan?.schedule?.[installmentIndex];
        const amt  = slot ? fmtMoney(slot.amount) : '';
        toast.success(`${tr.retrySuccess}${amt.replace(/^\$/, '')}`);
      } else if (status === 'requires_action') {
        toast.error(tr.retryFailed + ' — 3DS required');
      } else {
        toast.error(tr.retryFailed);
      }
    } catch (err) {
      console.error('retryInstallment error:', err);
      toast.error(err?.message || tr.retryFailed);
    } finally {
      setRetryingIdx(null);
    }
  };

  /**
   * Total already-paid cents across the schedule. This is the upper bound
   * for any refund and drives the modal's "Paid so far" display + the
   * validation on partial-refund input.
   */
  const totalPaidCents = useMemo(() => {
    const sched = reg.paymentPlan?.schedule;
    if (!Array.isArray(sched)) return 0;
    return sched
      .filter(s => s.status === 'paid')
      .reduce((a, s) => a + (s.amount || 0), 0);
  }, [reg]);

  /**
   * Coach-triggered cancel + optional refund. Delegates all Stripe work to
   * the server (`cancelRegistration` callable). Partial amounts validated
   * client-side before send to fail fast on typos.
   */
  const confirmCancel = async () => {
    let refundCents = null;
    if (refundMode === 'partial') {
      const cents = Math.round(Number(partialAmount || 0) * 100);
      if (!cents || cents <= 0) { toast.error(tr.cancelExceedsPaid); return; }
      if (cents > totalPaidCents) { toast.error(tr.cancelExceedsPaid); return; }
      refundCents = cents;
    }
    setCancelling(true);
    try {
      const res = await httpsCallable(getFunctions(), 'cancelRegistration')({
        registrationId: reg.id,
        refundMode,
        refundCents,
        reason: cancelReason.trim() || null,
      });
      const refundTotal = res?.data?.refundTotal || 0;
      if (refundTotal > 0) {
        toast.success(`${tr.cancelToastRefunded}${fmtMoney(refundTotal)}`);
      } else {
        toast.success(tr.cancelToastSuccess);
      }
      setShowCancelModal(false);
      onStatusChange(reg.id, 'cancelled');
    } catch (err) {
      console.error('cancelRegistration error:', err);
      toast.error(err?.message || tr.cancelToastFail);
    } finally {
      setCancelling(false);
    }
  };

  const reinstateReg = async () => {
    setReinstating(true);
    try {
      const res = await httpsCallable(getFunctions(), 'reinstateRegistration')({ registrationId: reg.id });
      toast.success(tr.reinstateSuccess);
      onStatusChange(reg.id, res?.data?.status || 'confirmed');
    } catch (err) {
      console.error('reinstateRegistration error:', err);
      toast.error(err?.message || tr.reinstateFail);
    } finally {
      setReinstating(false);
    }
  };

  const resendEmail = async () => {
    if (!reg.parentEmail) { toast.error(tr.toasts.noEmail); return; }
    try {
      setResendingEmail(true);
      await httpsCallable(getFunctions(), 'sendRegistrationConfirmation')({ registrationId: reg.id });
      toast.success(`${tr.toasts.emailResentTo} ${reg.parentEmail}`);
    } catch (err) {
      console.error('resendEmail error:', err);
      toast.error(tr.toasts.failSendEmail);
    } finally {
      setResendingEmail(false);
    }
  };

  const isArchived = reg.status === 'archived';

  // Primary status actions (per-status)
  const statusActions = {
    pending_payment: [{ label: tr.markConfirmed, status: 'confirmed', color: 'bg-green-600 text-white hover:bg-green-700' }],
    confirmed:       [{ label: tr.markCancelled, status: 'cancelled', color: 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' }],
    paid:            [{ label: tr.markCancelled, status: 'cancelled', color: 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' }],
    archived:        [],
  };
  const actions = statusActions[reg.status] || [];
  // Was a refund issued? If so, reinstate is blocked — family must re-register.
  const hadRefund = reg.status === 'cancelled' && (reg.cancelRefundTotal || 0) > 0;

  // Player initials for the lime medallion in the pitch-dark header.
  const playerInitials = (reg.playerName || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';
  const jersey = reg.jerseyNumber ?? reg.jersey ?? null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header — pitch-dark with lime grid + medallion */}
        <div className="px-5 pt-5 pb-5 bg-[#0d1f14] relative overflow-hidden">
          {/* lime grid overlay */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#b5f03d]"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 16 }}
              >
                {jersey ? `#${jersey}` : playerInitials}
              </div>
              <div className="min-w-0">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-1"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {tr.player}
                </p>
                <h2
                  className="uppercase text-white leading-[1.05] truncate"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 22 }}
                >
                  {reg.playerName}
                </h2>
                <div className="mt-2">
                  <StatusBadge status={reg.status} tr={tr} />
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 inline-flex items-center justify-center rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/10 hover:bg-[#b5f03d]/20 text-[#b5f03d] h-9 w-9 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-white">

          {/* Archived notice */}
          {isArchived && (
            <div className="flex items-center gap-2 rounded-xl bg-zinc-100 px-3 py-2.5 text-sm text-zinc-600">
              <ArchiveBoxIcon className="h-4 w-4 shrink-0" />
              {tr.archivedNotice}
            </div>
          )}

          {/* Player */}
          <section>
            <h3
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.player}
            </h3>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt
                  className="text-[11px] uppercase text-zinc-400"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                >
                  {tr.name}
                </dt>
                <dd className="font-medium text-[#0d1f14]">{reg.playerName}</dd>
              </div>
              {reg.playerDOB && (
                <div className="flex justify-between">
                  <dt
                    className="text-[11px] uppercase text-zinc-400"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    {tr.dob}
                  </dt>
                  <dd className="font-medium text-[#0d1f14]">{format(new Date(reg.playerDOB), 'MM/dd/yyyy')}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Parent */}
          <section>
            <h3
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.parentGuardian}
            </h3>
            <div className="space-y-2.5">
              <div className="flex items-start gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <UserGroupIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className="text-[11px] uppercase text-zinc-400"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    {tr.name}
                  </p>
                  <p className="text-[#0d1f14] font-medium">{reg.parentName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <EnvelopeIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className="text-[11px] uppercase text-zinc-400"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    {tr.email}
                  </p>
                  <a href={`mailto:${reg.parentEmail}`} className="text-[#5a8a1f] hover:text-[#3d6010] hover:underline break-all">{reg.parentEmail}</a>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <PhoneIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className="text-[11px] uppercase text-zinc-400"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    {tr.phone}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a href={`tel:${reg.parentPhone}`} className="text-[#5a8a1f] hover:text-[#3d6010] hover:underline">{reg.parentPhone}</a>
                    {/* SMS opt-in indicator — coaches can see at a glance whether
                        this parent will receive text alerts without opening the
                        full registration or checking the dashboard filter. */}
                    {reg.smsOptIn === true && isValidUsE164(reg.parentPhone || '')
                      ? <span title={tr.smsOptInStatus.on}  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-emerald-50 text-emerald-700 border-emerald-200" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>SMS</span>
                      : reg.smsOptIn === true
                        ? <span title={tr.smsOptInStatus.badPhone} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-amber-50 text-amber-700 border-amber-200" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>{tr.smsOptInStatus.badPhoneShort}</span>
                        : <span title={tr.smsOptInStatus.off} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-zinc-50 text-zinc-600 border-zinc-200" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>No SMS</span>
                    }
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Emergency Contact */}
          {(reg.emergencyContact?.name || reg.emergencyName) && (
            <section>
              <h3
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.emergencyContact}
              </h3>
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <dt
                    className="text-[11px] uppercase text-zinc-400"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    {tr.name}
                  </dt>
                  <dd className="font-medium text-[#0d1f14]">{reg.emergencyContact?.name || reg.emergencyName}</dd>
                </div>
                <div className="flex justify-between">
                  <dt
                    className="text-[11px] uppercase text-zinc-400"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    {tr.relationship}
                  </dt>
                  <dd className="font-medium text-[#0d1f14]">{reg.emergencyContact?.relationship ?? '—'}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt
                    className="text-[11px] uppercase text-zinc-400 flex items-center gap-1"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    <PhoneIcon className="h-3.5 w-3.5" />{tr.phone}
                  </dt>
                  <dd>
                    <a href={`tel:${reg.emergencyContact?.phone || reg.emergencyPhone}`} className="text-[#5a8a1f] hover:text-[#3d6010] hover:underline">{reg.emergencyContact?.phone || reg.emergencyPhone}</a>
                  </dd>
                </div>
              </dl>
            </section>
          )}

          {/* Medical Notes */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] flex items-center gap-1"
                style={{ fontFamily: HEAD_FONT }}
              >
                <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-500" />
                {tr.medicalNotes}
              </h3>
              <button
                onClick={() => setEditNotes(v => !v)}
                className="text-xs text-[#5a8a1f] hover:text-[#3d6010] hover:underline flex items-center gap-0.5"
              >
                <PencilSquareIcon className="h-3.5 w-3.5" /> {tr.edit}
              </button>
            </div>
            {editNotes ? (
              <div className="space-y-2">
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={saveNotes}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    {tr.save}
                  </button>
                  <button
                    onClick={() => { setEditNotes(false); setNotes(reg.medicalNotes || ''); }}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    {tr.cancel}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#0d1f14] bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 min-h-[36px]">
                {notes || <span className="text-zinc-400 italic">{tr.noneNoted}</span>}
              </p>
            )}
          </section>

          {/* Pricing breakdown */}
          {(reg.pricing || reg.amountDue) && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] flex items-center gap-1"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  <BanknotesIcon className="h-3.5 w-3.5" />{tr.pricing}
                </h3>
                {season?.financialAidEnabled && !isArchived && (
                  <button
                    onClick={() => setEditAid(v => !v)}
                    className="text-xs text-[#5a8a1f] hover:text-[#3d6010] hover:underline flex items-center gap-0.5"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" />
                    {editAid ? tr.cancel : tr.applyAid}
                  </button>
                )}
              </div>

              <dl className="space-y-1 text-sm bg-[#f7faf5] border border-zinc-200 rounded-xl px-3 py-2.5">
                <div className="flex justify-between">
                  <dt className="text-zinc-500">{tr.basePrice}</dt>
                  <dd className="text-[#0d1f14]">{fmtMoney(reg.pricing?.baseAmount ?? reg.amountDue)}</dd>
                </div>
                {(reg.pricing?.earlyBirdSaving > 0) && (
                  <div className="flex justify-between text-emerald-700">
                    <dt className="flex items-center gap-1"><SparklesIcon className="h-3 w-3" />{tr.earlyBird}</dt>
                    <dd>−{fmtMoney(reg.pricing.earlyBirdSaving)}</dd>
                  </div>
                )}
                {(reg.pricing?.siblingDiscount > 0) && (
                  <div className="flex justify-between text-emerald-700">
                    <dt>{tr.siblingDiscount}</dt>
                    <dd>−{fmtMoney(reg.pricing.siblingDiscount)}</dd>
                  </div>
                )}
                {(reg.pricing?.returningDiscount > 0) && (
                  <div className="flex justify-between text-emerald-700">
                    <dt>{tr.returningPlayer}</dt>
                    <dd>−{fmtMoney(reg.pricing.returningDiscount)}</dd>
                  </div>
                )}
                {(reg.pricing?.financialAidDiscount > 0) && (
                  <div className="flex justify-between text-[#3d6010]">
                    <dt>{tr.financialAid}{reg.financialAidNote ? ` (${reg.financialAidNote})` : ''}</dt>
                    <dd>−{fmtMoney(reg.pricing.financialAidDiscount)}</dd>
                  </div>
                )}
                <div
                  className="flex justify-between font-bold text-[#0d1f14] border-t border-zinc-200 pt-1 mt-1 uppercase"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <dt>{tr.total}</dt>
                  <dd>{fmtMoney(reg.pricing?.finalAmount ?? reg.amountDue)}</dd>
                </div>
                {reg.paymentPlan && (
                  <div className="flex justify-between text-xs text-zinc-400">
                    <dt>{reg.paymentPlan.installments}-{tr.installmentPlan}</dt>
                    <dd>{fmtMoney(reg.paymentPlan.firstInstallment)} {tr.firstPayment}</dd>
                  </div>
                )}
              </dl>

              {/* Financial aid editor */}
              {editAid && (
                <div className="mt-2 p-3 rounded-xl border border-[#b5f03d]/30 bg-[#b5f03d]/5 space-y-2">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#3d6010]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {tr.applyFinancialAid}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 text-sm">$</span>
                    <input type="number" min="0" step="0.01" value={aidAmount}
                      onChange={e => setAidAmount(e.target.value)} placeholder="0.00"
                      className="w-24 rounded-lg border border-zinc-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
                    <span className="text-xs text-zinc-500">{tr.discountDollars}</span>
                  </div>
                  <input type="text" value={aidNote} onChange={e => setAidNote(e.target.value)}
                    placeholder={tr.reasonOptional}
                    className="w-full rounded-lg border border-zinc-300 px-2 py-1.5 text-xs focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
                  <div className="flex gap-2">
                    <button
                      onClick={saveFinancialAid}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                    >
                      {tr.save}
                    </button>
                    <button
                      onClick={() => setEditAid(false)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {tr.cancel}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Payment plan tracker — schedule-aware (Level 2 auto-charge) */}
          {reg.paymentPlan && reg.installmentsTotal > 1 && (() => {
            const schedule = Array.isArray(reg.paymentPlan.schedule) ? reg.paymentPlan.schedule : null;

            /* Legacy fallback for plans created before Level 2 (no `schedule`
             * array). Keeps the old count-based renderer with manual "Mark
             * Paid" — useful when coach collected cash outside Stripe. */
            if (!schedule) {
              return (
                <section>
                  <h3
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {tr.paymentPlan} — {reg.installmentsTotal} {tr.installments}
                  </h3>
                  <div className="space-y-1.5">
                    {Array.from({ length: reg.installmentsTotal }).map((_, i) => {
                      const n = i + 1;
                      const paid = n <= (reg.installmentsPaid ?? 0);
                      const isFirst = n === 1;
                      return (
                        <div key={n} className="flex items-center justify-between text-sm">
                          <span className="text-[#0d1f14]">
                            {tr.installment} {n}
                            <span className="text-xs text-zinc-400 ml-1">
                              ({fmtMoney(isFirst ? reg.paymentPlan.firstInstallment : reg.paymentPlan.installmentAmount)})
                            </span>
                          </span>
                          {paid ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-emerald-50 text-emerald-700 border-emerald-200"
                              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                            >
                              <CheckCircleIcon className="h-3 w-3" />
                              {isFirst ? tr.paidViaStripe : tr.paidLabel}
                            </span>
                          ) : n === (reg.installmentsPaid ?? 0) + 1 ? (
                            <button
                              onClick={markInstallmentPaid}
                              className="text-xs font-bold text-[#5a8a1f] hover:text-[#3d6010] hover:underline uppercase"
                              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                            >
                              {tr.markPaid}
                            </button>
                          ) : (
                            <span className="text-xs text-zinc-400">{tr.pendingLabel}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            }

            // Pretty-print a Firestore Timestamp or Date-ish value.
            const fmtSlotDate = (v) => {
              if (!v) return null;
              const d = v.toDate?.() || (v.seconds ? new Date(v.seconds * 1000) : new Date(v));
              if (Number.isNaN(d.getTime?.())) return null;
              return format(d, 'MMM d, yyyy', dateLocale ? { locale: dateLocale } : undefined);
            };

            const paidCount = schedule.filter(s => s.status === 'paid').length;
            const hasPastDue = reg.status === 'past_due' || schedule.some(s => s.status === 'failed');
            const nextPending = schedule.find(s => s.status === 'pending' || s.status === 'failed');

            return (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {tr.paymentPlan} — {paidCount}/{reg.installmentsTotal} {tr.paidLabel.toLowerCase()}
                  </h3>
                  {hasPastDue && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-red-50 text-red-700 border-red-200"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                    >
                      <ExclamationTriangleIcon className="h-3 w-3" />
                      {tr.pastDueLabel}
                    </span>
                  )}
                </div>

                {/* Next charge hint — only if there's an unpaid slot */}
                {nextPending && (
                  <div className="mb-2 text-xs text-zinc-500">
                    {tr.nextChargeLabel}: <span className="font-medium text-[#0d1f14]">{fmtSlotDate(nextPending.dueDate) || '—'}</span>
                    <span className="text-zinc-400 ml-1">({fmtMoney(nextPending.amount)})</span>
                  </div>
                )}

                <div className="space-y-1.5 bg-[#f7faf5] border border-zinc-200 rounded-xl px-3 py-2.5">
                  {schedule.map((slot, i) => {
                    const n = i + 1;
                    const status = slot.status || 'pending';
                    const due = fmtSlotDate(slot.dueDate);
                    const attempts = slot.attemptCount || 0;
                    const isRetrying = retryingIdx === i;

                    return (
                      <div key={n} className="flex items-center justify-between text-sm">
                        <span className="text-[#0d1f14] truncate">
                          {tr.installment} {n}
                          <span className="text-xs text-zinc-400 ml-1">
                            ({fmtMoney(slot.amount)}{due ? ` · ${due}` : ''})
                          </span>
                        </span>

                        {status === 'paid' ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-emerald-50 text-emerald-700 border-emerald-200"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                          >
                            <CheckCircleIcon className="h-3 w-3" />
                            {tr.paidLabel}
                          </span>
                        ) : status === 'refunded' ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-zinc-50 text-zinc-700 border-zinc-200"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                          >
                            <CheckCircleIcon className="h-3 w-3 text-zinc-300" />
                            {tr.refundedLabel}
                          </span>
                        ) : status === 'partially_refunded' ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-amber-50 text-amber-700 border-amber-200"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                            title={slot.refundedAmount ? `−${fmtMoney(slot.refundedAmount)}` : ''}
                          >
                            {tr.partiallyRefundedLabel}
                            {slot.refundedAmount && (
                              <span className="text-zinc-400 font-normal normal-case">
                                (−{fmtMoney(slot.refundedAmount)})
                              </span>
                            )}
                          </span>
                        ) : status === 'failed' ? (
                          <button
                            onClick={() => retryInstallment(i)}
                            disabled={isRetrying}
                            className="text-xs font-bold text-red-700 hover:underline disabled:opacity-50 disabled:no-underline uppercase"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                            title={slot.failureReason || ''}
                          >
                            {isRetrying ? tr.retrying : tr.retryNow}
                            {attempts > 0 && !isRetrying && (
                              <span className="text-zinc-400 font-normal ml-1 normal-case">({attempts} {tr.attemptsLabel})</span>
                            )}
                          </button>
                        ) : status === 'cancelled' ? (
                          <span className="text-xs text-zinc-400">—</span>
                        ) : (
                          <span className="text-xs text-zinc-400">
                            {attempts > 0 ? `${tr.pendingLabel} · ${attempts} ${tr.attemptsLabel}` : tr.scheduledLabel}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })()}

          {/* Documents */}
          {(() => {
            const required = season?.requiredDocuments ?? [];
            const uploadUrl = `${window.location.origin}/upload/${season?.teamId}/${reg.id}`;
            const copyUploadLink = () => {
              navigator.clipboard.writeText(uploadUrl).then(() => toast.success(tr.toasts.uploadLinkCopied));
            };
            if (required.length === 0) return null;
            const uploaded = required.filter(k => reg.documents?.[k]);
            const allDone = uploaded.length === required.length;
            return (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] flex items-center gap-1"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    <DocumentCheckIcon className="h-3.5 w-3.5" />
                    {tr.documents} ({uploaded.length}/{required.length})
                  </h3>
                  <button
                    onClick={copyUploadLink}
                    className="flex items-center gap-1 text-xs text-[#5a8a1f] hover:text-[#3d6010] hover:underline"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    {tr.copyUploadLink}
                  </button>
                </div>
                <div className="space-y-1.5 bg-[#f7faf5] border border-zinc-200 rounded-xl px-3 py-2.5">
                  {required.map(key => {
                    const label = tr.docLabels[key] ?? key;
                    const icon = DOC_ICONS[key] ?? '📄';
                    const done = !!reg.documents?.[key];
                    return (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span className="text-[#0d1f14] flex items-center gap-1.5">
                          <span>{icon}</span>
                          {label}
                        </span>
                        {done ? (
                          <a
                            href={reg.documents[key]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 transition-colors"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                          >
                            <CheckCircleIcon className="h-3 w-3" />
                            {tr.viewDoc}
                          </a>
                        ) : (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-red-50 text-red-700 border-red-200"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                          >
                            {tr.missing}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!allDone && (
                  <button
                    onClick={copyUploadLink}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-4 py-2.5 text-xs font-bold uppercase text-[#3d6010] hover:bg-[#b5f03d]/20 transition-all"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    <CloudArrowUpIcon className="h-4 w-4" />
                    {tr.sendUploadLinkToParent}
                  </button>
                )}
              </section>
            );
          })()}

          {/* Registration info */}
          <section>
            <h3
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.registrationInfo}
            </h3>
            <dl className="space-y-1.5 text-sm">
              {reg.division && (
                <div className="flex justify-between">
                  <dt
                    className="text-[11px] uppercase text-zinc-400"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    {tr.division}
                  </dt>
                  <dd className="font-medium text-[#0d1f14]">{reg.division}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt
                  className="text-[11px] uppercase text-zinc-400"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                >
                  {tr.registered}
                </dt>
                <dd className="text-[#0d1f14]">{reg.createdAt ? format(toDate(reg.createdAt), 'MMM d, yyyy h:mm a', dateLocale) : '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt
                  className="text-[11px] uppercase text-zinc-400"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                >
                  {tr.waiverSignedBy}
                </dt>
                <dd className="text-[#0d1f14]">{reg.waiverSignedByName ?? '—'}</dd>
              </div>
            </dl>
          </section>

          {/* SMS send history — visible to coaches; useful for support
              ("did the payment-failed text actually go out?") and TCPA
              audits ("when did this parent last receive a text from us?"). */}
          <section>
            <h3
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3 flex items-center gap-1"
              style={{ fontFamily: HEAD_FONT }}
            >
              <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
              {tr.smsHistory.title}
            </h3>
            {smsLogsLoading ? (
              <p className="text-xs text-zinc-400">{tr.smsHistory.loading}</p>
            ) : smsLogs.length === 0 ? (
              <p className="text-xs text-zinc-400">{tr.smsHistory.empty}</p>
            ) : (
              <ul className="space-y-1.5">
                {smsLogs.map(log => {
                  const sentAt = log.sentAt?.toDate?.() || null;
                  const dateStr = sentAt
                    ? format(sentAt, 'MMM d, h:mm a', dateLocale)
                    : '—';
                  const flowLabel = tr.smsHistory.flows[log.flow] || tr.smsHistory.flows.unknown;
                  return (
                    <li key={log.id} className="rounded-xl bg-white border border-zinc-200 px-3 py-2 text-xs flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-[#0d1f14] flex items-center gap-1 flex-wrap">
                          {flowLabel}
                          {log.urgent && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-amber-50 text-amber-700 border-amber-200"
                              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                            >
                              {tr.smsHistory.urgent}
                            </span>
                          )}
                        </div>
                        {log.bodyPreview && (
                          <div className="text-zinc-500 truncate mt-0.5" title={log.bodyPreview}>
                            {log.bodyPreview}
                          </div>
                        )}
                        <div className="text-zinc-400 mt-0.5">{dateStr}</div>
                      </div>
                      <span
                        className={clsx(
                          'inline-flex items-center shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border',
                          log.status === 'sent'   ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          log.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200' :
                                                    'bg-zinc-50 text-zinc-700 border-zinc-200'
                        )}
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                      >
                        {log.status === 'sent' ? tr.smsHistory.statusSent : tr.smsHistory.statusFailed}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* Footer — delete confirmation */}
        {confirmDelete ? (
          <div className="border-t border-red-200 bg-red-50 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p
                  className="text-sm font-bold text-red-700 uppercase"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {tr.deletePermanently}
                </p>
                <p className="text-xs text-red-600 mt-0.5">
                  {tr.deleteWarnPrefix} <strong>{reg.playerName}</strong> {tr.deleteWarnSuffix}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={changingStatus}
                className="flex-1 inline-flex items-center justify-center rounded-xl bg-red-600 text-white px-4 py-2.5 text-sm font-bold uppercase hover:bg-red-700 disabled:opacity-50 transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {changingStatus ? tr.deleting : tr.yesDelete}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {tr.cancel}
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-zinc-200 p-4 space-y-2 bg-white">
            {/* Add to Roster */}
            {!reg.rosterAdded && onAddToRoster && !isArchived && (
              <button
                onClick={() => onAddToRoster(reg)}
                disabled={changingStatus}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white px-4 py-2.5 text-sm font-bold uppercase hover:bg-emerald-500 transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                <UserPlusIcon className="h-4 w-4" />
                {tr.addToRoster}
              </button>
            )}
            {reg.rosterAdded && (
              <div
                className="flex items-center justify-center gap-2 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-bold uppercase border border-emerald-200"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <CheckCircleIcon className="h-4 w-4" />
                {tr.addedToRoster}
              </div>
            )}

            {/* Resend confirmation email */}
            {reg.parentEmail && !isArchived && (
              <button
                onClick={resendEmail}
                disabled={resendingEmail || changingStatus}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 disabled:opacity-50 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <EnvelopeIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />
                {resendingEmail ? tr.sending : tr.resendEmail}
              </button>
            )}

            {/* Primary status actions */}
            {actions.length > 0 && (
              <div className="flex gap-2">
                {actions.map(action => {
                  const isCancel = action.status === 'cancelled';
                  // Confirm = lime CTA, Cancel = ghost-danger
                  const cls = isCancel
                    ? 'flex-1 inline-flex items-center justify-center rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-red-600 hover:bg-red-50 transition-all disabled:opacity-50'
                    : 'flex-1 inline-flex items-center justify-center rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50';
                  return (
                    <button
                      key={action.status}
                      onClick={() => {
                        if (isCancel) {
                          setRefundMode(totalPaidCents > 0 ? 'full' : 'none');
                          setPartialAmount('');
                          setCancelReason('');
                          setShowCancelModal(true);
                        } else {
                          changeStatus(action.status);
                        }
                      }}
                      disabled={changingStatus}
                      className={cls}
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                    >
                      {changingStatus ? tr.updating : action.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Cancelled registration — two paths depending on whether a refund was issued */}
            {reg.status === 'cancelled' && (
              hadRefund ? (
                // Refund was issued: can't reinstate, must re-register from scratch.
                <div className="rounded-xl border border-zinc-200 bg-[#f7faf5] px-4 py-3 space-y-2">
                  <p className="text-xs text-[#0d1f14]">{tr.reEnrollNote}</p>
                  <button
                    onClick={() => {
                      const link = `${window.location.origin}/register/${reg.teamId}/${reg.seasonId}`;
                      navigator.clipboard.writeText(link).then(() => toast.success(tr.toasts.regLinkCopied));
                    }}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    <LinkIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />
                    {tr.sendRegLinkBtn}
                  </button>
                </div>
              ) : (
                // No refund: can reinstate — restores schedule and resumes charges.
                <button
                  onClick={reinstateReg}
                  disabled={reinstating}
                  className="w-full inline-flex items-center justify-center rounded-xl bg-emerald-600 text-white px-4 py-2.5 text-sm font-bold uppercase hover:bg-emerald-500 disabled:opacity-50 transition-all active:scale-[0.97]"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {reinstating ? tr.reinstating : tr.reinstate}
                </button>
              )
            )}

            {/* Archive / Restore + Delete row */}
            <div className="flex gap-2">
              {isArchived ? (
                <button
                  onClick={() => changeStatus('confirmed')}
                  disabled={changingStatus}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 disabled:opacity-50 transition-all"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <ArrowUturnLeftIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />
                  {tr.restoreRegistration}
                </button>
              ) : (
                <button
                  onClick={() => changeStatus('archived')}
                  disabled={changingStatus}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 disabled:opacity-50 transition-all"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <ArchiveBoxIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />
                  {tr.archiveBtn}
                </button>
              )}
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={changingStatus}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-white px-4 py-2 text-xs font-bold uppercase text-red-600 hover:bg-red-50 disabled:opacity-50 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <TrashIcon className="h-3.5 w-3.5" />
                {tr.deleteBtn}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel + refund modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !cancelling && setShowCancelModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header — pitch-dark with lime grid + medallion */}
            <div className="px-5 pt-5 pb-5 bg-[#0d1f14] relative overflow-hidden">
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.05]"
                style={{
                  backgroundImage:
                    'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
                  backgroundSize: '32px 32px',
                }}
              />
              <div className="relative flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-500/15 ring-1 ring-red-500/40 text-red-400">
                  <ExclamationTriangleIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-1"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {tr.statuses.cancelled}
                  </p>
                  <h3
                    className="uppercase text-white leading-[1.05]"
                    style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 20 }}
                  >
                    {tr.cancelModalTitle}
                  </h3>
                  <p className="mt-1 text-xs text-zinc-300">{tr.cancelModalIntro}</p>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
              {/* Paid-so-far summary */}
              {totalPaidCents > 0 && (
                <div className="rounded-xl bg-[#f7faf5] border border-zinc-200 px-3 py-2 flex justify-between text-sm">
                  <span
                    className="text-[11px] uppercase text-zinc-500 self-center"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.8px', fontWeight: 700 }}
                  >
                    {tr.cancelPaidSoFar}
                  </span>
                  <span
                    className="font-bold text-[#0d1f14] uppercase"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    {fmtMoney(totalPaidCents)}
                  </span>
                </div>
              )}

              {/* Option: full refund (only if anything paid) */}
              {totalPaidCents > 0 && (
                <label className={clsx(
                  'flex items-start gap-2.5 cursor-pointer rounded-xl border p-3 transition-all',
                  refundMode === 'full'
                    ? 'border-[#b5f03d] bg-[#b5f03d]/5'
                    : 'border-zinc-200 hover:border-[#b5f03d]/60'
                )}>
                  <input
                    type="radio"
                    name="refundMode"
                    value="full"
                    checked={refundMode === 'full'}
                    onChange={() => setRefundMode('full')}
                    className="mt-0.5 accent-[#b5f03d]"
                  />
                  <div>
                    <div className="text-sm font-medium text-[#0d1f14]">
                      {tr.cancelRefundFull}{' '}
                      <span className="text-zinc-500 font-normal">({fmtMoney(totalPaidCents)})</span>
                    </div>
                  </div>
                </label>
              )}

              {/* Option: partial refund */}
              {totalPaidCents > 0 && (
                <label className={clsx(
                  'flex items-start gap-2.5 cursor-pointer rounded-xl border p-3 transition-all',
                  refundMode === 'partial'
                    ? 'border-[#b5f03d] bg-[#b5f03d]/5'
                    : 'border-zinc-200 hover:border-[#b5f03d]/60'
                )}>
                  <input
                    type="radio"
                    name="refundMode"
                    value="partial"
                    checked={refundMode === 'partial'}
                    onChange={() => setRefundMode('partial')}
                    className="mt-0.5 accent-[#b5f03d]"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#0d1f14]">{tr.cancelRefundPartial}</div>
                    {refundMode === 'partial' && (
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={(totalPaidCents / 100).toFixed(2)}
                        value={partialAmount}
                        onChange={e => setPartialAmount(e.target.value)}
                        placeholder={tr.cancelAmountPlaceholder}
                        className="mt-2 w-full rounded-lg border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                      />
                    )}
                  </div>
                </label>
              )}

              {/* Option: no refund */}
              <label className={clsx(
                'flex items-start gap-2.5 cursor-pointer rounded-xl border p-3 transition-all',
                refundMode === 'none'
                  ? 'border-[#b5f03d] bg-[#b5f03d]/5'
                  : 'border-zinc-200 hover:border-[#b5f03d]/60'
              )}>
                <input
                  type="radio"
                  name="refundMode"
                  value="none"
                  checked={refundMode === 'none'}
                  onChange={() => setRefundMode('none')}
                  className="mt-0.5 accent-[#b5f03d]"
                />
                <div>
                  <div className="text-sm font-medium text-[#0d1f14]">{tr.cancelRefundNone}</div>
                  <div className="mt-0.5 text-xs text-zinc-500">{tr.cancelRefundNoneHint}</div>
                </div>
              </label>

              {/* Internal reason */}
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                rows={2}
                placeholder={tr.cancelReasonPlaceholder}
                className="w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 resize-none"
              />
            </div>

            <div className="px-5 py-4 border-t border-zinc-200 flex gap-2 bg-white">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
                className="flex-1 inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 disabled:opacity-50 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {tr.cancelKeepBtn}
              </button>
              <button
                onClick={confirmCancel}
                disabled={cancelling}
                className="flex-1 inline-flex items-center justify-center rounded-xl bg-red-600 text-white px-4 py-2.5 text-sm font-bold uppercase hover:bg-red-700 disabled:opacity-50 transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {cancelling ? tr.cancelingBtn : tr.cancelConfirmBtn}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── main component ──────────────────────────────────── */
export default function RegistrationsDashboard() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const tr = T[lang];
  const dateLocale = lang === 'es' ? { locale: esLocale } : undefined;

  const [season,          setSeason]          = useState(null);
  const [team,            setTeam]            = useState(null);
  const [registrations,   setRegistrations]   = useState([]);
  const [programRegs,     setProgramRegs]     = useState([]);   // program regs for this team
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState('');
  const [filter,          setFilter]          = useState('all');
  const [selectedReg,     setSelectedReg]     = useState(null);
  const [sortField,       setSortField]       = useState('createdAt');
  const [sortDir,         setSortDir]         = useState('desc');
  const [broadcastOpen,   setBroadcastOpen]   = useState(false);

  /* ── load season ─────────────────────────────────── */
  useEffect(() => {
    async function loadSeason() {
      const snap = await getDoc(doc(db, 'seasons', seasonId));
      if (!snap.exists()) return;
      const s = { id: snap.id, ...snap.data() };
      setSeason(s);
      if (s.teamId) {
        const tSnap = await getDoc(doc(db, 'teams', s.teamId));
        if (tSnap.exists()) setTeam({ id: tSnap.id, ...tSnap.data() });
      }
    }
    loadSeason();
  }, [seasonId]);

  /* ── live season registrations ────────────────────
   *
   * Must filter by BOTH seasonId AND teamId. The Phase 1 lockdown of the
   * `registrations` collection gates reads on isTeamStaff(resource.data.teamId);
   * Firestore's rules engine can't statically prove a seasonId-only query
   * is safe (the rule branch wraps teamId in a function call, so it isn't
   * pattern-matchable against a query without the teamId equality clause).
   * Without the teamId filter the listener fails permission-denied and the
   * dashboard renders the "No registrations yet" empty state silently.
   *
   * Depends on `season?.teamId` so we wait for the season doc to load
   * before firing the query.
   */
  useEffect(() => {
    if (!seasonId || !season?.teamId) return;
    const q = query(
      collection(db, 'registrations'),
      where('seasonId', '==', seasonId),
      where('teamId', '==', season.teamId),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setRegistrations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      // Surface this in dev — silently empty state was the regression that
      // hid the original bug. In prod we still fall through to empty state
      // but at least leave a breadcrumb.
      if (import.meta.env.DEV) {
        console.warn('[RegistrationsDashboard] season registrations subscribe failed', err);
      }
      setLoading(false);
    });
    return unsub;
  }, [seasonId, season?.teamId]);

  /* ── program registrations for this team (seasonId: null) ── */
  useEffect(() => {
    if (!team?.id) { setProgramRegs([]); return; }
    const q = query(
      collection(db, 'registrations'),
      where('teamId', '==', team.id),
      where('seasonId', '==', null),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setProgramRegs(snap.docs.map((d) => ({ id: d.id, ...d.data(), _isProgram: true })));
    }, () => setProgramRegs([]));
    return unsub;
  }, [team?.id]);

  /* ── sort handler ────────────────────────────────── */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  /* ── merged list (season + program regs) ─────────── */
  const allRegistrations = useMemo(
    () => [...registrations, ...programRegs],
    [registrations, programRegs]
  );

  /* ── derived stats (excluding archived) ──────────── */
  const stats = useMemo(() => {
    const active   = allRegistrations.filter(r => r.status !== 'archived');
    const total    = active.length;
    const paid     = active.filter(r => ['paid', 'confirmed'].includes(r.status)).length;
    const pending  = active.filter(r => r.status === 'pending_payment').length;
    const archived = allRegistrations.filter(r => r.status === 'archived').length;
    const programCount = programRegs.filter(r => r.status !== 'archived').length;
    // Level 2 payment plans: surface anything with a failed slot OR past_due status.
    const pastDue  = active.filter(r =>
      r.status === 'past_due' ||
      (Array.isArray(r.paymentPlan?.schedule) && r.paymentPlan.schedule.some(s => s.status === 'failed'))
    ).length;

    const activeAndPaid = active.filter(r => ['paid', 'confirmed'].includes(r.status));

    // contracted = total registration fee for all paid/confirmed regs, including
    // future unpaid installments. Answers "what will this season earn when fully paid?"
    const contracted = activeAndPaid
      .reduce((sum, r) => sum + (r.pricing?.finalAmount ?? r.amountDue ?? 0), 0);

    // collected = cash actually received to date.
    //   • Pay-in-full regs: amountDue (charged in one shot by Stripe).
    //   • Installment-plan regs: sum only the slots that have status 'paid' —
    //     future pending/failed slots haven't been captured yet and must not
    //     inflate the collected figure.
    const collected = activeAndPaid.reduce((sum, r) => {
      const isInstallmentPlan =
        Array.isArray(r.paymentPlan?.schedule) && (r.installmentsTotal ?? 1) > 1;
      if (isInstallmentPlan) {
        return sum + r.paymentPlan.schedule.reduce(
          (s, slot) => s + (slot.status === 'paid' ? (slot.amount ?? 0) : 0),
          0
        );
      }
      return sum + (r.pricing?.finalAmount ?? r.amountDue ?? 0);
    }, 0);

    return { total, paid, pending, collected, contracted, archived, programCount, pastDue };
  }, [allRegistrations, programRegs]);

  /* ── Tiered-pricing breakdown ─────────────────────────────
   * Per-tier counts + revenue collected, only computed for seasons
   * running on time-based tiered pricing. Counts use the same
   * paid+confirmed filter as the main stats card; revenue uses
   * pricing.finalAmount when present (covers discounts + fees), falling
   * back to amountDue for older registrations missing the breakdown.
   *
   * We attribute by reg.pricingTierId. Legacy registrations from before
   * Phase 2 won't have the field — those land in an "Unattributed" row
   * so coaches can see at a glance which regs predate this rollout.
   */
  const tierBreakdown = useMemo(() => {
    if (!season || season.pricingMode !== 'tiered' || !Array.isArray(season.pricingTiers)) {
      return null;
    }
    const tiers = [...season.pricingTiers].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );
    const paidActive = allRegistrations.filter(
      r => r.status !== 'archived' && ['paid', 'confirmed'].includes(r.status)
    );
    const rows = tiers.map((t) => {
      const matched = paidActive.filter(r => r.pricingTierId === t.id);
      const collected = matched.reduce(
        (sum, r) => sum + (r.pricing?.finalAmount ?? r.amountDue ?? 0),
        0
      );
      const filled = matched.length;
      return {
        id:         t.id,
        name:       t.name,
        priceCents: t.priceInCents,
        count:      filled,
        collected,
        cap:        t.maxRegistrations ?? null,
        spotsLeft:  t.maxRegistrations != null
          ? Math.max(0, t.maxRegistrations - filled)
          : null,
      };
    });
    // Catch-all for legacy or partially-stamped registrations so revenue
    // adds up cleanly across the table.
    const unattributed = paidActive.filter(r => !r.pricingTierId || !tiers.find(t => t.id === r.pricingTierId));
    if (unattributed.length > 0) {
      rows.push({
        id: '__unattributed',
        name: 'Unattributed',
        priceCents: null,
        count: unattributed.length,
        collected: unattributed.reduce(
          (sum, r) => sum + (r.pricing?.finalAmount ?? r.amountDue ?? 0),
          0
        ),
        cap: null,
        spotsLeft: null,
      });
    }
    return rows;
  }, [season, allRegistrations]);

  /* ── SMS reachability — active parents with opt-in + valid phone ──
   * Shown as a count badge on the Text Parents button so coaches can
   * see at-a-glance how much of the roster is actually reachable by
   * text before they open the compose modal. Mirrors the server-side
   * filter in listSmsEligibleParentsForTeam. */
  const smsReady = useMemo(() => {
    const active = allRegistrations.filter(r => r.status !== 'archived' && r.status !== 'cancelled');
    const reachable = active.filter(r => r.smsOptIn === true && isValidUsE164(r.parentPhone || '')).length;
    return { reachable, total: active.length };
  }, [allRegistrations]);

  /* ── docs-pending count (computed after season loads) ── */
  const docsPending = useMemo(() => {
    const required = season?.requiredDocuments ?? [];
    if (required.length === 0) return 0;
    return allRegistrations.filter(r =>
      r.status !== 'archived' &&
      required.some(k => !r.documents?.[k])
    ).length;
  }, [allRegistrations, season]);

  /* ── filtered + sorted list ──────────────────────── */
  const filtered = useMemo(() => {
    let list = allRegistrations;
    if (filter === 'all') {
      list = list.filter(r => r.status !== 'archived'); // hide archived by default
    } else if (filter === 'past_due') {
      // Virtual filter: any reg with status past_due OR a failed schedule slot.
      list = list.filter(r =>
        r.status !== 'archived' && (
          r.status === 'past_due' ||
          (Array.isArray(r.paymentPlan?.schedule) && r.paymentPlan.schedule.some(s => s.status === 'failed'))
        )
      );
    } else {
      list = list.filter(r => r.status === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.playerName?.toLowerCase().includes(q) ||
        r.parentName?.toLowerCase().includes(q) ||
        r.parentEmail?.toLowerCase().includes(q)
      );
    }
    // Sort
    list = [...list].sort((a, b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (sortField === 'createdAt') {
        av = toDate(av)?.getTime() ?? 0;
        bv = toDate(bv)?.getTime() ?? 0;
      } else {
        av = (av ?? '').toString().toLowerCase();
        bv = (bv ?? '').toString().toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [registrations, filter, search, sortField, sortDir]);

  /* ── roster helpers ─────────────────────────────── */
  const buildPlayerDoc = (reg) => {
    const { firstName, lastName } = splitName(reg.playerName);
    return {
      firstName,
      lastName,
      parentName:     reg.parentName   || '',
      parentEmail:    reg.parentEmail  || '',
      parentPhone:    reg.parentPhone  || '',
      language:       'en',
      source:         'registration',
      registrationId: reg.id,
      // Carry the headshot the parent uploaded during registration
      // (required-doc "Player Photo") onto the roster player so it shows
      // on the teammates card. Null when no photo was uploaded.
      photoURL:       reg.documents?.player_photo?.url || null,
      notes: [
        reg.playerDOB   ? `DOB: ${reg.playerDOB}`   : '',
        reg.medicalNotes || '',
      ].filter(Boolean).join(' | ') || '',
      createdAt: serverTimestamp(),
    };
  };

  // Dedup guard: don't create a duplicate of a player already on the
  // roster (manually added, or from a prior registration). Strong match:
  // same non-empty parent email. Weak fallback: exactly ONE normalized
  // full-name match (0 or ≥2 → don't guess, let it create a new player —
  // a wrong link is worse than an occasional dup). Returns the matched
  // player {id, ...data} or null.
  const normStr = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const findRosterMatch = async (teamId, reg) => {
    const snap = await getDocs(collection(db, 'teams', teamId, 'players')).catch(() => null);
    if (!snap) return null;
    const players = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const regEmail = normStr(reg.parentEmail);
    if (regEmail) {
      const byEmail = players.find(p => normStr(p.parentEmail) === regEmail);
      if (byEmail) return byEmail;
    }
    const regName = normStr(reg.playerName || `${reg.playerFirstName || ''} ${reg.playerLastName || ''}`);
    if (regName) {
      const byName = players.filter(p => normStr(`${p.firstName || ''} ${p.lastName || ''}`) === regName);
      if (byName.length === 1) return byName[0];
    }
    return null;
  };

  const handleAddToRoster = async (reg) => {
    if (!season?.teamId) { toast.error(tr.toasts.noTeamLinked); return; }
    try {
      const match = await findRosterMatch(season.teamId, reg);
      if (match) {
        // Link to the existing player instead of duplicating. Backfill the
        // photo only if the player doesn't already have one (don't clobber).
        await updateDoc(doc(db, 'teams', season.teamId, 'players', match.id), {
          registrationId: reg.id,
          photoURL: match.photoURL || reg.documents?.player_photo?.url || null,
          updatedAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'registrations', reg.id), { rosterAdded: true, playerId: match.id });
        toast.success(`${reg.playerName} ${tr.toasts.linkedToRoster}`);
      } else {
        await addDoc(
          collection(db, 'teams', season.teamId, 'players'),
          buildPlayerDoc(reg),
        );
        await updateDoc(doc(db, 'registrations', reg.id), { rosterAdded: true });
        toast.success(`${reg.playerName} ${tr.toasts.playerAddedToRoster}`);
      }
      setRegistrations(prev =>
        prev.map(r => r.id === reg.id ? { ...r, rosterAdded: true } : r)
      );
      setSelectedReg(prev =>
        prev?.id === reg.id ? { ...prev, rosterAdded: true } : prev
      );
    } catch (err) {
      console.error(err);
      toast.error(tr.toasts.couldNotAddRoster);
    }
  };

  const handleAddAllToRoster = async () => {
    if (!season?.teamId) { toast.error(tr.toasts.noTeamLinked); return; }
    const eligible = registrations.filter(
      r => ['confirmed', 'paid'].includes(r.status) && !r.rosterAdded
    );
    if (eligible.length === 0) {
      toast(tr.toasts.allAlreadyOnRoster);
      return;
    }
    const batch = writeBatch(db);
    const playersRef = collection(db, 'teams', season.teamId, 'players');

    // Dedup: fetch the current roster once and match each eligible
    // registration against it (email, then single-name) so we link
    // existing players instead of creating duplicates. Track names/emails
    // claimed within THIS batch so two registrations for the same kid
    // don't both create a new player.
    const existingSnap = await getDocs(playersRef).catch(() => null);
    const existing = existingSnap ? existingSnap.docs.map(d => ({ id: d.id, ...d.data() })) : [];
    const claimedEmails = new Set();
    const claimedNames = new Set();
    const matchInRoster = (reg) => {
      const email = normStr(reg.parentEmail);
      if (email) {
        const m = existing.find(p => normStr(p.parentEmail) === email);
        if (m) return m;
      }
      const name = normStr(reg.playerName || `${reg.playerFirstName || ''} ${reg.playerLastName || ''}`);
      if (name) {
        const m = existing.filter(p => normStr(`${p.firstName || ''} ${p.lastName || ''}`) === name);
        if (m.length === 1) return m[0];
      }
      return null;
    };

    eligible.forEach(reg => {
      const email = normStr(reg.parentEmail);
      const name = normStr(reg.playerName || `${reg.playerFirstName || ''} ${reg.playerLastName || ''}`);
      const match = matchInRoster(reg);
      if (match) {
        batch.update(doc(playersRef, match.id), {
          registrationId: reg.id,
          photoURL: match.photoURL || reg.documents?.player_photo?.url || null,
          updatedAt: serverTimestamp(),
        });
        batch.update(doc(db, 'registrations', reg.id), { rosterAdded: true, playerId: match.id });
      } else if ((email && claimedEmails.has(email)) || (name && claimedNames.has(name))) {
        // Already being created earlier in this same batch — just flag the
        // registration; don't create a second duplicate player.
        batch.update(doc(db, 'registrations', reg.id), { rosterAdded: true });
      } else {
        batch.set(doc(playersRef), buildPlayerDoc(reg));
        batch.update(doc(db, 'registrations', reg.id), { rosterAdded: true });
        if (email) claimedEmails.add(email);
        if (name) claimedNames.add(name);
      }
    });
    try {
      await batch.commit();
      setRegistrations(prev =>
        prev.map(r => eligible.some(e => e.id === r.id) ? { ...r, rosterAdded: true } : r)
      );
      const suffix = eligible.length === 1 ? tr.toasts.playersAddedSingular : tr.toasts.playersAddedPlural;
      toast.success(`${eligible.length} ${suffix}`);
    } catch (err) {
      console.error(err);
      toast.error(tr.toasts.batchFailed);
    }
  };

  /* ── CSV export ──────────────────────────────────── */
  const exportCSV = () => {
    const headers = ['Player Name', 'DOB', 'Parent Name', 'Email', 'Phone', 'Emergency Contact', 'Emergency Phone', 'Relationship', 'Medical Notes', 'Status', 'Amount Due', 'Registered'];
    const rows = filtered.map(r => [
      r.playerName ?? '',
      r.playerDOB ?? '',
      r.parentName ?? '',
      r.parentEmail ?? '',
      r.parentPhone ?? '',
      r.emergencyContact?.name ?? '',
      r.emergencyContact?.phone ?? '',
      r.emergencyContact?.relationship ?? '',
      (r.medicalNotes ?? '').replace(/,/g, ';'),
      r.status ?? '',
      r.amountDue ? (r.amountDue / 100).toFixed(2) : '0.00',
      r.createdAt ? format(toDate(r.createdAt), 'yyyy-MM-dd') : '',
    ]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${season?.name ?? 'registrations'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${tr.toasts.exportedPrefix} ${filtered.length} ${tr.toasts.exportedSuffix}`);
  };

  /* ── copy all visible emails ─────────────────────── */
  const copyEmails = () => {
    const emails = [...new Set(filtered.map(r => r.parentEmail).filter(Boolean))];
    if (!emails.length) { toast.error(tr.toasts.noEmailsToCopy); return; }
    navigator.clipboard.writeText(emails.join(', ')).then(() => {
      const suffix = emails.length === 1 ? tr.toasts.emailCopied : tr.toasts.emailsCopied;
      toast.success(`${emails.length} ${suffix}`);
    });
  };

  /* ── copy registration link ──────────────────────── */
  const copyLink = () => {
    if (!season?.teamId) return;
    const url = `${window.location.origin}/register/${season.teamId}/${seasonId}`;
    navigator.clipboard.writeText(url).then(() => toast.success(tr.toasts.regLinkCopied));
  };

  /* ── Bulk-send action bar config ──
   * Only render when the active filter narrows the list to a meaningful subset
   * we can act on. New URL params (audience=unpaid|missing_docs, seasonId) are
   * a TODO contract for ComposerWizard to consume. For now it routes correctly
   * and the composer falls back to its own audience picker.
   *
   * MUST live above the `if (loading)` early return below — React's rules of
   * hooks require every render to call the same hooks in the same order.
   * Originally this useMemo was placed AFTER the early return, which caused
   * Minified React error #310 ("rendered more hooks than during the previous
   * render") the moment `loading` flipped from true to false.
   */
  const bulkSendAction = useMemo(() => {
    if (!season?.teamId || !seasonId) return null;
    const teamPath = `/teams/${season.teamId}/messages/new`;
    if (filter === 'pending_payment') {
      const count = filtered.filter(r => r.status === 'pending_payment').length;
      if (!count) return null;
      return {
        label: `Remind all ${count} awaiting payment`,
        count,
        href: `${teamPath}?type=Payment%20Due&audience=unpaid&seasonId=${seasonId}`,
      };
    }
    if (filter === 'past_due') {
      const count = filtered.length;
      if (!count) return null;
      return {
        label: `Remind all ${count} past-due`,
        count,
        href: `${teamPath}?type=Payment%20Due&audience=unpaid&seasonId=${seasonId}`,
      };
    }
    // Missing-docs subset — virtual: only meaningful when the season has
    // required docs configured and the current filter list contains regs
    // missing one of them.
    const required = season?.requiredDocuments ?? [];
    if (required.length > 0) {
      const missing = filtered.filter(r =>
        r.status !== 'archived' && r.status !== 'cancelled' &&
        required.some(k => !r.documents?.[k])
      );
      if (filter !== 'all' && filter !== 'paid' && filter !== 'confirmed' && missing.length > 0) {
        return {
          label: `Email all ${missing.length} with incomplete docs`,
          count: missing.length,
          href: `${teamPath}?type=Reminder&audience=missing_docs&seasonId=${seasonId}`,
        };
      }
    }
    return null;
  }, [filter, filtered, season, seasonId]);

  /* ── render ──────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="p-4 sm:p-6 lg:p-8 space-y-4">
          {[1,2,3].map(n => <div key={n} className="h-16 rounded-2xl bg-white border border-zinc-200 animate-pulse" />)}
        </div>
      </div>
    );
  }

  const FILTER_TABS = [
    { key: 'all',             label: tr.filterTabs.all },
    { key: 'confirmed',       label: tr.filterTabs.confirmed },
    { key: 'pending_payment', label: tr.filterTabs.pending_payment },
    { key: 'paid',            label: tr.filterTabs.paid },
    // Only show the past-due tab when there's actually something to triage —
    // otherwise it's just visual noise for the majority of coaches.
    ...(stats.pastDue > 0 ? [{ key: 'past_due', label: `${tr.filterTabs.past_due} (${stats.pastDue})`, danger: true }] : []),
    { key: 'cancelled',       label: tr.filterTabs.cancelled },
    { key: 'archived',        label: `${tr.filterTabs.archived}${stats.archived > 0 ? ` (${stats.archived})` : ''}` },
  ];

  const sortProps = { currentField: sortField, dir: sortDir, onSort: handleSort };

  /* Reusable ghost-button class for coach utility actions in the top action bar. */
  const ghostBtn = "inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-zinc-200 disabled:hover:bg-white";

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={season?.teamId} />

      {/* Back + header */}
      <div className="mb-6">
        {/* Back goes to the per-team seasons list (which has TeamSubNav)
            instead of the global /seasons (which doesn't). Falls back to
            global if season hasn't loaded yet. */}
        <button
          onClick={() => navigate(season?.teamId ? `/teams/${season.teamId}/seasons` : '/seasons')}
          className="mb-3 flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" /> {tr.backToSeasons}
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {season?.name ?? tr.registrationsFallback}
            </h1>
            {team?.name && (
              <p className="text-sm text-zinc-500 mt-0.5">{team.name}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Add all confirmed players to roster — semantic emerald (success action) */}
            {season?.teamId && registrations.some(r => ['confirmed', 'paid'].includes(r.status) && !r.rosterAdded) && (
              <button
                type="button"
                onClick={handleAddAllToRoster}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold uppercase text-white hover:bg-emerald-500 transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <UserPlusIcon className="h-4 w-4" />
                {tr.addAllToRoster}
              </button>
            )}
            <button
              type="button"
              onClick={copyLink}
              className={ghostBtn}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <LinkIcon className="h-4 w-4 text-[#5a8a1f]" />
              {tr.copyRegLink}
            </button>
            <button
              type="button"
              onClick={copyEmails}
              disabled={filtered.length === 0}
              className={ghostBtn}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <EnvelopeIcon className="h-4 w-4 text-[#5a8a1f]" />
              {tr.copyEmails}
            </button>
            {season?.teamId && (
              <button
                type="button"
                onClick={() => setBroadcastOpen(true)}
                disabled={registrations.length === 0}
                title={smsReady.total > 0
                  ? tr.textParentsTooltip
                      .replace('{reachable}', String(smsReady.reachable))
                      .replace('{total}', String(smsReady.total))
                  : tr.textParents}
                className={ghostBtn}
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4 text-[#5a8a1f]" />
                {tr.textParents}
                {smsReady.total > 0 && (
                  <span className={clsx(
                    'ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                    smsReady.reachable === 0
                      ? 'bg-zinc-100 text-zinc-500'
                      : 'bg-[#b5f03d]/20 text-[#3d6010]'
                  )}>
                    {smsReady.reachable}/{smsReady.total}
                  </span>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={exportCSV}
              disabled={filtered.length === 0}
              className={ghostBtn}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <ArrowDownTrayIcon className="h-4 w-4 text-[#5a8a1f]" />
              {tr.exportCSV}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className={clsx('grid gap-3 mb-6', docsPending > 0 ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4')}>
        <StatCard icon={UserGroupIcon}     label={tr.stats.activeTotal} value={stats.total} />
        <StatCard icon={CheckCircleIcon}   label={tr.stats.confirmed}   value={stats.paid} />
        <StatCard icon={ClockIcon}         label={tr.stats.pending}     value={stats.pending} />
        <StatCard
          icon={CurrencyDollarIcon}
          label={tr.stats.revenue}
          value={formatFee(stats.collected, tr)}
          sub={stats.contracted !== stats.collected
            ? tr.stats.contracted.replace('{amount}', formatFee(stats.contracted, tr))
            : null}
        />
        {docsPending > 0 && (
          <StatCard icon={DocumentCheckIcon} label={tr.stats.docsMissing} value={docsPending} sub={tr.stats.players} />
        )}
      </div>

      {/* Tiered-pricing per-tier breakdown — only renders when the season
          is configured with time-based pricing tiers. Sits below the main
          stat cards so coaches see at a glance how revenue spreads across
          the early-bird / regular / late tiers. */}
      {tierBreakdown && tierBreakdown.length > 0 && (
        <div className="mb-6 bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-zinc-100">
            <CurrencyDollarIcon className="h-4 w-4 text-[#5a8a1f]" />
            <h3
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Pricing Tier Breakdown
            </h3>
          </div>
          <div className="divide-y divide-zinc-100">
            {tierBreakdown.map((row) => (
              <div key={row.id} className="px-5 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-bold uppercase text-[#0d1f14] truncate"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
                  >
                    {row.name}{row.priceCents != null ? ` (${formatTierPrice(row.priceCents)})` : ''}
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {row.count} paid · {formatFee(row.collected, tr)} collected
                  </p>
                </div>
                <span
                  className={clsx(
                    'shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase border',
                    row.cap == null
                      ? 'bg-zinc-50 text-zinc-600 border-zinc-200'
                      : row.spotsLeft === 0
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : row.spotsLeft != null && row.spotsLeft < 5
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  )}
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {row.cap == null
                    ? 'Unlimited'
                    : `${row.count} of ${row.cap} spots filled`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + search */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlassIcon className="pointer-events-none absolute inset-y-0 left-3 h-full w-4 text-zinc-400 z-10" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={tr.searchPlaceholder}
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-white border border-zinc-300 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/30 focus:border-[#b5f03d] transition-colors"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTER_TABS.map(tab => {
            const isActive = filter === tab.key;
            // Danger (past-due) keeps semantic red so it remains alarming;
            // everything else uses the unified pitch/lime chip system.
            const activeClass = tab.danger
              ? 'bg-red-600 text-white border-red-600'
              : 'bg-[#0d1f14] text-white border-[#0d1f14]';
            const idleClass = tab.danger
              ? 'bg-red-50 border-red-200 text-red-700 hover:border-red-400'
              : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#b5f03d]/40';
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={clsx(
                  'text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full border transition-colors',
                  isActive ? activeClass : idleClass
                )}
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 rounded-2xl bg-white p-10 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
            <UserGroupIcon className="h-7 w-7 text-[#5a8a1f]" />
          </div>
          <h3
            className="mt-4 text-lg font-bold uppercase text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {filter === 'archived' ? tr.noArchived : tr.noRegistrations}
          </h3>
          <p className="mt-1 text-sm text-zinc-500">
            {filter === 'archived'
              ? tr.noArchivedBody
              : tr.noRegistrationsBody}
          </p>
          {filter !== 'archived' && (
            <button
              type="button"
              onClick={copyLink}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:bg-[#94cc28] transition-colors shadow-[0_2px_8px_-2px_rgba(181,240,61,0.6)]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <LinkIcon className="h-4 w-4" />
              {tr.copyRegistrationLink}
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden hover:border-[#b5f03d]/40 transition-all">
          <Table striped>
            <TableHead>
              <TableRow>
                <SortableHeader field="playerName" {...sortProps}>{tr.tableHeaders.player}</SortableHeader>
                <SortableHeader field="playerDOB"  {...sortProps}>{tr.tableHeaders.dob}</SortableHeader>
                <SortableHeader field="parentName" {...sortProps}>{tr.tableHeaders.parent}</SortableHeader>
                <TableHeader>{tr.tableHeaders.contact}</TableHeader>
                <SortableHeader field="status"     {...sortProps}>{tr.tableHeaders.status}</SortableHeader>
                {(season?.requiredDocuments?.length > 0) && (
                  <TableHeader>{tr.tableHeaders.docs}</TableHeader>
                )}
                <SortableHeader field="createdAt"  {...sortProps}>{tr.tableHeaders.registered}</SortableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow
                  key={r.id}
                  className={clsx(
                    'cursor-pointer hover:bg-zinc-50 transition-colors',
                    r.status === 'archived' && 'opacity-60'
                  )}
                  onClick={() => setSelectedReg(r)}
                >
                  <TableCell className="font-medium text-[#0d1f14]">
                    {r.playerName}
                    {r._isProgram && (
                      <span
                        className="ml-1.5 inline-block rounded-full bg-[#b5f03d]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#3d6010] tracking-wide"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        {tr.programPill}
                      </span>
                    )}
                    {r.medicalNotes && (
                      <span
                        className="ml-1.5 inline-block rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 tracking-wide"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        {tr.medicalPill}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-500">
                    {r.playerDOB ? format(new Date(r.playerDOB), 'MM/dd/yyyy') : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-zinc-800">{r.parentName}</div>
                    <div className="text-xs text-zinc-400">{r.parentEmail}</div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">{r.parentPhone}</TableCell>
                  <TableCell><StatusBadge status={r.status} tr={tr} /></TableCell>
                  {(season?.requiredDocuments?.length > 0) && (
                    <TableCell><DocStatusPill reg={r} season={season} /></TableCell>
                  )}
                  <TableCell className="text-xs text-zinc-400">
                    {r.createdAt ? format(toDate(r.createdAt), 'MMM d, yyyy', dateLocale) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="border-t border-zinc-200 px-4 py-3 text-xs text-zinc-500 flex items-center justify-between">
            <span>{tr.showing} {filtered.length} {tr.of} {allRegistrations.length} {tr.totalLabel}{programRegs.length > 0 ? ` (${tr.includingProgram} ${programRegs.length} ${tr.programLabel})` : ''}</span>
            {stats.archived > 0 && filter !== 'archived' && (
              <button
                onClick={() => setFilter('archived')}
                className="flex items-center gap-1 text-zinc-500 hover:text-[#5a8a1f] transition-colors"
              >
                <ArchiveBoxIcon className="h-3.5 w-3.5" />
                {stats.archived} {tr.archivedCountSuffix}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Registration detail slide-over */}
      {selectedReg && (
        <RegistrationPanel
          reg={selectedReg}
          season={season}
          onClose={() => setSelectedReg(null)}
          onStatusChange={(id, newStatus) => {
            setRegistrations(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
            setSelectedReg(prev => prev?.id === id ? { ...prev, status: newStatus } : prev);
          }}
          onDelete={(id) => {
            setRegistrations(prev => prev.filter(r => r.id !== id));
            setSelectedReg(null);
          }}
          onAddToRoster={season?.teamId ? handleAddToRoster : null}
          tr={tr}
          dateLocale={dateLocale}
        />
      )}

      {/* Coach broadcast SMS modal */}
      <BroadcastSmsModal
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        teamId={season?.teamId || null}
        seasonId={seasonId}
        registrations={registrations}
        strings={tr.broadcast}
      />

      {/* Bulk-send sticky action bar — appears when the current filter narrows
          the list to an actionable subset (unpaid, past-due, or missing-docs). */}
      {bulkSendAction && (
        <div
          className="sticky bottom-0 left-0 right-0 z-20 mt-4 -mx-4 sm:-mx-6 lg:-mx-8 border-t border-zinc-200 bg-white/95 backdrop-blur px-4 sm:px-6 lg:px-8 py-3 shadow-[0_-2px_8px_-2px_rgba(13,31,20,0.08)]"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-[#b5f03d]/15 border border-[#b5f03d]/40 px-2.5 py-1 text-[11px] font-bold uppercase text-[#3d6010]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {bulkSendAction.count} selected
            </span>
            <button
              type="button"
              onClick={() => navigate(bulkSendAction.href)}
              aria-label={bulkSendAction.label}
              className="ml-auto inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_2px_8px_-2px_rgba(181,240,61,0.6)] transition-all active:scale-[0.97] w-full sm:w-auto justify-center"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
              {bulkSendAction.label}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
